import { Drawer } from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import { BackgroundProcessProvider } from "contexts/BackgroundProcessContext";
import { memo, ReactNode, useState } from "react";
import MainContainer from "../MainContainer";
import Navbar from "../Navbar";
import Sidebar from "../Sidebar";

interface MainLayoutProps {
  children: ReactNode;
}

const DashboardLayout = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  "& .MuiAppBar-root": {
    zIndex: theme.zIndex.drawer + 1,
    "& .MuiTypography-root": {
      flexGrow: 1,
    },
  },
  // Permanent sidebar styles (lg+ only)
  "& .nephro-permanent-drawer": {
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      backgroundColor: "transparent !important",
      width: "75px",
      height: "calc(100% - 89px)",
      marginTop: "83px",
      boxSizing: "border-box",
      borderRight: "none",
      transition: "width 0.3s ease-in-out",
    },
  },
  "& .MuiListItem-root": {
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(3),
  },
  "& .MuiListItemIcon-root": {
    minWidth: theme.spacing(4),
    marginRight: theme.spacing(2),
    color: "#3245B4",
  },
  "& .MuiCheckbox-root:hover, & .MuiRadio-root:hover": {
    backgroundColor: "transparent",
  },
  ".MuiAutocomplete-hasPopupIcon.css-1koza9f-MuiAutocomplete-root .MuiOutlinedInput-root": {
    padding: "8.5px !important",
  },
}));

// Permanent desktop sidebar
const StyledDrawer = styled(Drawer)({
  flexShrink: 0,
  overflow: "hidden",
  zIndex: 1200,
  "& .MuiDrawer-paper": {
    width: "75px",
    height: "calc(100% - 89px)",
    marginTop: "83px",
    backgroundColor: "transparent !important",
    boxSizing: "border-box",
    borderRight: "none",
    transition: "width 0.3s ease-in-out",
  },
});

const MainLayout = ({ children }: MainLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <BackgroundProcessProvider>
      <DashboardLayout data-testid="layout-container">
        {/* Desktop: permanent icon-only sidebar */}
        <StyledDrawer variant="permanent" anchor="left" sx={{ display: { xs: "none", lg: "block" } }}>
          <Sidebar />
        </StyledDrawer>

        {/* Mobile/tablet: temporary overlay drawer with icon + label */}
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              width: "240px",
              marginTop: "48px",
              height: "calc(100% - 48px)",
              background: "linear-gradient(116.41deg, #eff4fb 16.09%, #f7f3ff 44.79%, #eff4fb 59.94%, #f7f4ff 95.81%)",
              boxShadow: "4px 0 20px rgba(0,0,0,0.10)",
              borderRight: "none",
              boxSizing: "border-box",
            },
          }}>
          <Sidebar showLabels onItemClick={() => setMobileOpen(false)} />
        </Drawer>

        <MainContainer marginLeft="93px" marginTop={{ xs: "48px", lg: "70px" }}>
          {children}
        </MainContainer>
        <Navbar onMenuToggle={() => setMobileOpen((prev) => !prev)} />
      </DashboardLayout>
    </BackgroundProcessProvider>
  );
};

export default memo(MainLayout);
